import { ARTILLERY_ENDPOINT } from '@Env/variables';
import { createAxiosInstance } from './axios';
import {
  ArtilleryReport,
  AnalyzerWeeklyPrompt,
  ArtilleryEmailRecipients,
} from '@Models/types';

export const ArtilleryAPI = (logout: () => void) => {
  const axios = createAxiosInstance(logout, ARTILLERY_ENDPOINT);

  return {
    getReports: async (): Promise<ArtilleryReport[]> => {
      const response = await axios.get('/api/fetchReportsAPI');
      return response.data;
    },

    downloadPDF: async (fileName: string): Promise<void> => {
      const response = await axios.get(
        `/api/downloadPDF?fileName=${fileName}`,
        {
          responseType: 'blob',
        }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${fileName}.pdf`);
      document.body.appendChild(link);
      link.click();
    },

    // Prompts
    getPrompts: async (): Promise<AnalyzerWeeklyPrompt[]> => {
      const { data } = await axios.get('/api/artillery/prompts');
      return data;
    },

    createPrompt: async (
      prompt: Omit<
        AnalyzerWeeklyPrompt,
        '_id' | 'authorEmail' | 'dateCreated' | 'isDefault'
      >
    ) => {
      const { data } = await axios.post('/api/artillery/prompts', prompt);
      return data;
    },

    updatePrompt: async (
      originalTitle: string,
      originalAuthor: string,
      prompt: Partial<AnalyzerWeeklyPrompt>
    ) => {
      const { data } = await axios.post('/api/artillery/prompts/update', {
        originalTitle,
        originalAuthor,
        ...prompt,
      });
      return data;
    },

    deletePrompt: async (promptTitle: string, authorEmail: string) => {
      const { data } = await axios.post('/api/artillery/prompts/delete', {
        promptTitle,
        authorEmail,
      });
      return data;
    },

    setDefaultPrompt: async (promptTitle: string, authorEmail: string) => {
      const { data } = await axios.post('/api/artillery/prompts/setDefault', {
        promptTitle,
        authorEmail,
      });
      return data;
    },

    // Email Recipients
    getEmailRecipients: async (): Promise<ArtilleryEmailRecipients[]> => {
      const { data } = await axios.get('/api/artillery/emails');
      return data;
    },

    createEmailRecipient: async (
      recipient: Omit<ArtilleryEmailRecipients, '_id' | 'dateCreated'>
    ) => {
      const { data } = await axios.post('/api/artillery/emails', recipient);
      return data;
    },

    updateEmailRecipient: async (
      originalEmail: string,
      recipient: Omit<ArtilleryEmailRecipients, '_id' | 'dateCreated'>
    ) => {
      const { data } = await axios.post('/api/artillery/emails/update', {
        originalEmail,
        ...recipient,
      });
      return data;
    },

    deleteEmailRecipient: async (email: string) => {
      const { data } = await axios.post('/api/artillery/emails/delete', {
        email,
      });
      return data;
    },
  };
};
